<template>
  <div :class="['lesson-chat-history', menuStyle, browserName, { iPad: isIpadDevice }]">
    <LessonHeader />
    <div id="content" class="content">
      <div class="chatlog">
        <div class="chatlog_info" v-if="lessonDetails">
            <div class="chatlog_date">
            <p>{{ lessonDetails.date_time }}</p>
            </div>
            <div class="chatlog_ttl">
            <p>{{ lessonHeaderText }}</p>
            </div>
        </div>
        <div v-if="lessonResultDetail" class="lesson_result_notification">
          <span class="lesson_result_icon"><font-awesome-icon icon="exclamation-circle" /></span>
          <span class="lesson_result_text">{{ lessonResultDetail }}</span>
        </div>
        <perfect-scrollbar class="chat_ui scrollable">
            <div v-for="(item, index) in messageAndEvents" :key="index">
              <div v-if="item.dateTime">
  
                  <div v-if="item.__typename === 'ChatMessage'">
                    <div v-if="isStudent" :class="item.from === 's' ? 'sent_chat' : 'received_chat'">
                        <el-avatar class="received_icon" v-if="item.from === 't'">
                          {{ getFirstLetterNickName(lessonDetails.teacher_nickname) }}
                        </el-avatar>

                        <div :class="item.from === 's' ? 'sent_chat_main' : 'received_chat_main'">
                          <span class="chat_time">{{ item.dateTime.formattedTime }}</span>
                          <div :class="['chat_balloon', { msg_del: item.deleted }]">
                              <p v-if="item.deleted">{{ $t('chatBox.deletedMessage') }}</p>
                              <p v-else v-dompurify-html="item.message"></p>
                          </div>
                        </div>

                        <el-avatar class="sent_icon" v-if="item.from === 's'">
                          {{ getFirstLetterNickName(lessonDetails.student_nickname) }}
                        </el-avatar>
                    </div>
                    <div v-else :class="item.from === 't' ? 'sent_chat' : 'received_chat'">
                        <el-avatar class="received_icon" v-if="item.from === 's'">
                          {{ getFirstLetterNickName(lessonDetails.student_nickname) }}
                        </el-avatar>

                        <div :class="item.from === 't' ? 'sent_chat_main' : 'received_chat_main'">
                          <span class="chat_time">
                            {{ item.dateTime.formattedTime }}
                            <span v-if="item.deleted && isAdmin">
                              | {{ item.deleted_message || $t('chatBox.deletedMessage') }}
                            </span>
                            <span v-if="item.editted && isAdmin">
                              | <a @click="showChatHistoryDialog(item.message_id)">Edited</a>
                            </span>
                          </span>
                          <div :class="['chat_balloon', { msg_del: item.deleted }]">
                              <p v-if="item.deleted && !isAdmin">{{ $t('chatBox.deletedMessage') }}</p>
                              <p v-else v-dompurify-html="item.message"></p>
                          </div>
                          <div v-if="!item.deleted && isAdmin">
                            <span :id="'delete_box_' + item.message_id" class="delete_message" style="display:none;">
                              <span @click="deleteChatMessage(item.message_id)">Delete</span>
                              <span @click="showEditChatDialog(item.message_id, item.message)">Edit</span>
                            </span>
                            <span :id="'delete_button_' + item.message_id" class="delete_button">
                              <a class="delete_message_alert">
                                <img src="@/assets/img/three_dots@2x.png" @click="toggleAdminMenu(item.message_id)">
                              </a>
                            </span>
                          </div>
                        </div>

                        <el-avatar class="sent_icon" v-if="item.from === 't'">
                          {{ getFirstLetterNickName(lessonDetails.teacher_nickname) }}
                        </el-avatar>
                    </div>
                  </div>
                  
                  <div v-if="item.__typename === 'ChatEvents'">
                    <div class="system-events">
                        <div>
                          <span>[{{ item.dateTime.formattedTime }}] &nbsp;</span>
                          <span class="capitalize-first">{{ getEventInfo(item) }}</span>
                        </div>
                    </div>
                  </div>
              </div>
            </div>
        </perfect-scrollbar>
      </div>
    </div>
    <BottomMenu />
    <ModalMessageEdit :modal-show="showChatMessageEdit" />
    <ModalMessageHistory :modal-show="showChatMessageHistory" />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import UAParser from 'ua-parser-js';
import _ from 'lodash';
import { errorCodes } from '@/utils/constants';
import { 
  redirectToHome, 
  debounce, 
  removeSpecificHtmlTag,
} from '@/utils/common';
import LessonHeader from '@/components/ChatHistory/Header/LessonHeader';
import BottomMenu from '@/components/ChatHistory/Footer/BottomMenu';
import ModalMessageEdit from '@/components/Modal/ModalMessageEdit';
import ModalMessageHistory from '@/components/Modal/ModalMessageHistory';
import ChatHistory from '@/mixins/chatHistory';
import UserLocalTimezoneMixin from '@/mixins/userLocalTimezone';

export default {
  mixins: [ChatHistory, UserLocalTimezoneMixin],
  components: {
    ModalMessageEdit,
    ModalMessageHistory,
    LessonHeader,
    BottomMenu
  },
  data: () => {
    return {
      editMessageId: null,
      visibleAdminMenu: false,
      clickedAdminMenu: null,
    };
  },
  created() {
     this.normalizeMessages()
  },
  mounted() {
    this.checkMediaSize();
    this.checkIfIpadDevice();
    window.addEventListener('resize', debounce(this.checkMediaSize, 50));
    window.addEventListener('orientationchange', this.setLessonHeight);
    window.addEventListener('scroll', this.setLessonHeight);
    
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeMount: async function() {
    if (!this.isChatLoaded) {
      redirectToHome(errorCodes.ME002);
    }
  },
  computed: {
    ...mapState('modal', ['showChatMessageHistory', 'showChatMessageEdit']),
    ...mapState('layout', ['isIpadDevice', 'isMenuVisible']),
    ...mapGetters('chatHistory', ['isStudent', 'isAdmin', 'remoteUserNickname']),
    ...mapState('chatHistory', ['accountType', 'lessonDetails', 'messages', 'events', 'lessonId']),
  
    isChatLoaded() {
      return this.accountType !== null;
    },
    menuStyle() {
      if (this.isMenuVisible != null) {
        return this.isMenuVisible ? 'menu-open' : 'menu-close'
      }
      return '';
    },
    browserName() {
      const browserName = new UAParser().getBrowser().name;
      if (browserName.includes('Safari')) {
        return 'safari';
      }
      return browserName.toLowerCase();
    },
    messageAndEvents() {
      return _.sortBy([...this.messages, ...this.events], 'date_time');
    },
    msgFromTrainer() {
      return function (from) {
        return from === 't';
      }
    },
    getFirstLetterNickName() {
      return function (name) {
        return name?.charAt(0).toUpperCase();
      }
    },
    lessonHeaderText() {
      if (!this.lessonDetails.lesson_number) {
        return this.lessonDetails.program_name;
      }

      return `${this.lessonDetails.program_name} ${this.lessonDetails.level_rank} 
        Lesson ${this.lessonDetails.lesson_number} ${this.lessonDetails.lesson_title}`;
    },
    lessonResultDetail() {
      return this.$te(`lessonResult.${this.lessonDetails.result}`) ?
        this.$t(`lessonResult.${this.lessonDetails.result}`)
        : false;
    }
  },
  methods: {
    ...mapActions('layout', ['checkMediaSize', 'checkIfIpadDevice']),
    ...mapMutations('modal', ['setShowMessageHistory', 'setShowMessageEdit']),
    ...mapMutations('chatHistory', ['setCurrentChatHistoryId', 'setCurrentChatHistoryMeta']),

    normalizeMessages() {
      const messagesWithDateTime = this.messages.map(msg => ({
        ...msg,
        dateTime: this.toLocal(msg.date_time)
      }));
      this.$store.commit('chatHistory/setMessages', messagesWithDateTime);

      const eventsWithDateTime = this.events.map(event => ({
        ...event,
        dateTime: this.toLocal(event.date_time)
      }));
      this.$store.commit('chatHistory/setEvents', eventsWithDateTime);
    },
    setLessonHeight(event) {
      const value = event.type === 'scroll' ? '100%' : '100vh';
      document.querySelector('.lesson').style.height = value;
    },
    async showChatHistoryDialog(messageId) {
      messageId = parseInt(messageId);
      this.editMessageId = messageId;
      this.setCurrentChatHistoryId(messageId);
      this.setShowMessageHistory(true);
    },
    async showEditChatDialog(messageId, message) {
      messageId = parseInt(messageId);
      // clean-up `ql-noneditable` special HTML tag from the Lesson page chat tags
      message = removeSpecificHtmlTag(message, 'ql-noneditable');
      this.setCurrentChatHistoryMeta({messageId: messageId, message: message});
      this.setShowMessageEdit(true);
    },
    toggleAdminMenu(index) {
      this.visibleAdminMenu = true;
      document.querySelectorAll('.delete_message').forEach((el) => {
        el.style = 'display:none;';
      });

      document.querySelector('#delete_box_' + index).style = 'display:block;';
      this.clickedAdminMenu = document.querySelector('#delete_button_' + index + ' > a > img');
    },
    handleClickOutside(event) {
      if (this.visibleAdminMenu && ( !this.$el.contains(event.target) || event.target != this.clickedAdminMenu ))  {
        this.visibleAdminMenu = false;
        document.querySelectorAll('.delete_message').forEach((el) => {
          el.style = 'display:none;';
        });
      }
    },
    async deleteChatMessage(messageId) {
      await this.executeDeleteChatMessage({ lessonId: this.lessonId, messageId: messageId });
    },
    getEventInfo(messageItem) {
      if (this.isAdmin) {
        const nickname = messageItem.from === 's' 
          ? this.lessonDetails?.student_nickname 
          : this.lessonDetails?.teacher_nickname;
        return this.$t(`chatEvents.${messageItem.event}.remote`, [nickname]);
      }

      return messageItem.from === this.accountType
        ? this.$t(`chatEvents.${messageItem.event}.local`)
        : this.$t(`chatEvents.${messageItem.event}.remote`, [this.remoteUserNickname]);
    },
  },
  //disconnect on close
  beforeDestroy: function() {
    window.removeEventListener('resize', this.checkMediaSize);
    window.removeEventListener('orientationchange', this.setLessonHeight);
    window.removeEventListener('scroll', this.setLessonHeight);
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>
